import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPendingActions,
  getPendingActionsCount,
  type PendingActionsQuery,
  type PendingActionsView,
} from '../../api/pendingActions';

export interface UsePendingActionsResult {
  data: PendingActionsView | null;
  loading: boolean;
  /** Human-readable (translated or server-provided) load error. */
  error: string | null;
  /** True when the load failed at the transport layer (offline, timeout). */
  isNetworkError: boolean;
  /** Full reload with skeleton. */
  refresh: () => Promise<void>;
  /** Silent reload — keeps the current list on screen while refetching. */
  revalidate: () => Promise<void>;
  /** Append the next page onto the current list. */
  loadMore: () => Promise<void>;
  loadingMore: boolean;
}

/**
 * Fetches the unified Pending Actions inbox. Query changes trigger a fresh load;
 * `revalidate` refetches without a skeleton flash (focus/interval freshness).
 */
export function usePendingActions(query: PendingActionsQuery): UsePendingActionsResult {
  const { t } = useTranslation('requests');
  const [data, setData] = useState<PendingActionsView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const fetchIdRef = useRef(0);
  // Track the latest query for revalidate/loadMore without re-creating callbacks per keystroke.
  const queryRef = useRef(query);
  queryRef.current = query;

  const load = useCallback(async (silent: boolean) => {
    const fetchId = ++fetchIdRef.current;
    if (!silent) {
      setLoading(true);
      setError(null);
      setIsNetworkError(false);
    }
    try {
      const result = await getPendingActions({ ...queryRef.current, page: 1 });
      if (fetchId !== fetchIdRef.current) return;
      if (result.errors.length > 0) {
        // Prefer the server's message; never surface a raw code/literal to the user.
        const first = result.errors[0] as { message?: string; description?: string } | undefined;
        setError(first?.message || first?.description || t('Something went wrong'));
        setIsNetworkError(false);
      } else {
        setData(result.payload);
        setError(null);
        setIsNetworkError(false);
      }
    } catch {
      if (fetchId !== fetchIdRef.current) return;
      // Keep stale data on silent revalidation failures; only surface errors on explicit loads.
      if (!silent) {
        setError(t('Something went wrong'));
        setIsNetworkError(true);
      }
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [t]);

  const refresh = useCallback(() => load(false), [load]);
  const revalidate = useCallback(() => load(true), [load]);

  const loadMore = useCallback(async () => {
    const current = data;
    if (!current || current.items.length >= current.totalCount) return;
    setLoadingMore(true);
    const fetchId = ++fetchIdRef.current;
    try {
      const nextPage = current.page + 1;
      const result = await getPendingActions({ ...queryRef.current, page: nextPage });
      if (fetchId !== fetchIdRef.current) return;
      if (result.errors.length === 0) {
        setData({
          ...result.payload,
          items: [...current.items, ...result.payload.items],
        });
      }
    } catch {
      // Leave the list as-is; the user can retry via the button.
    } finally {
      if (fetchId === fetchIdRef.current) setLoadingMore(false);
    }
  }, [data]);

  useEffect(() => {
    // Reload whenever the serialized query changes.
    load(false);
  }, [query.groups, query.types, query.pageSize, load]);

  return { data, loading, error, isNetworkError, refresh, revalidate, loadMore, loadingMore };
}

/**
 * Cheap polled badge count (PendingActions/My/Count is side-effect-free by design).
 * 60s interval while visible + focus/visibility revalidation. Errors leave the
 * previous count in place — a badge must never break the chrome.
 */
export function usePendingActionsCount(pollMs = 60000): { count: number; refresh: () => Promise<void> } {
  const [count, setCount] = useState(0);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const result = await getPendingActionsCount();
      if (mountedRef.current && result.errors.length === 0) {
        setCount(result.payload ?? 0);
      }
    } catch {
      // Keep the last known count.
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const revalidateIfVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', revalidateIfVisible);
    document.addEventListener('visibilitychange', revalidateIfVisible);
    const interval = window.setInterval(revalidateIfVisible, pollMs);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('focus', revalidateIfVisible);
      document.removeEventListener('visibilitychange', revalidateIfVisible);
      window.clearInterval(interval);
    };
  }, [refresh, pollMs]);

  return { count, refresh };
}
